import React, { useState, useRef, useEffect, useCallback } from "react";
import IconButton from "@mui/material/IconButton";
import {
  Close,
  ChevronLeft,
  ChevronRight,
  ZoomIn,
  ZoomOut,
  Download,
  FitScreen,
} from "@mui/icons-material";
import { Tooltip } from "@mui/material";

export function PhotoLightbox({ photos = [], open, initialIndex = 0, onClose, auditPlan }) {
  const [index, setIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (open) {
      setIndex(initialIndex);
      resetView();
    }
  }, [open, initialIndex]);

  const goTo = useCallback((newIndex) => {
    const clamped = (newIndex + photos.length) % photos.length;
    setIndex(clamped);
    resetView();
  }, [photos.length]);

  useEffect(() => {
    if (!open) return;
    const handleKey = (e) => {
      if (e.key === "ArrowLeft") goTo(index - 1);
      else if (e.key === "ArrowRight") goTo(index + 1);
      else if (e.key === "Escape") onClose?.();
      else if (e.key === "+" || e.key === "=") zoomIn();
      else if (e.key === "-") zoomOut();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [open, index, scale, goTo, onClose]);

  const resetView = () => {
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  };


  const zoomIn = () => setScale((s) => Math.min(s + 0.3, 5));
  const zoomOut = () =>
    setScale((s) => {
      const next = Math.max(s - 0.3, 0.5);
      if (next === 1) setTranslate({ x: 0, y: 0 });
      return next;
    });

  const handleWheel = (e) => {
    e.preventDefault();
    if (e.deltaY < 0) zoomIn();
    else zoomOut();
  };

  const handleMouseDown = (e) => {
    if (scale <= 1) return;
    setIsDragging(true);
    setDragStart({ x: e.clientX - translate.x, y: e.clientY - translate.y });
  };
  const handleMouseMove = (e) => {
    if (!isDragging) return;
    setTranslate({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };
  const handleMouseUp = () => setIsDragging(false);

  // Download dengan watermark burn via canvas
  const handleDownloadSingle = async () => {
    try {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.src = current.file;

      await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = reject;
      });

      const canvas = document.createElement("canvas");
      canvas.width = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext("2d");

      ctx.drawImage(img, 0, 0);

      const w = canvas.width;
      const padding = 10;
      const lineHeight = Math.max(14, Math.floor(w * 0.022));

      const lines = [
        `${auditPlan?.store_code} · ${auditPlan?.store_type}`,
        auditPlan?.store_name || "",
        auditPlan?.audit_plan || "",
        `Surveyor: ${auditPlan?.user_full_name_surveyor} (${auditPlan?.user_name_surveyor})`,
        `${auditPlan?.audit_start_at} – ${auditPlan?.audit_end_at} · ${auditPlan?.audit_status}`,
      ].filter(Boolean);

      const boxHeight = lines.length * (lineHeight + 4) + padding * 2;

      // Strip di atas
      ctx.fillStyle = "rgba(0, 0, 0, 0.72)";
      ctx.fillRect(0, 0, w, boxHeight);

      ctx.textBaseline = "top";
      lines.forEach((line, i) => {
        const y = padding + i * (lineHeight + 4); // mulai dari atas
        if (i === 0) {
          ctx.fillStyle = "#0ab9cc";
          ctx.font = `bold ${lineHeight}px monospace`;
        } else if (i === 1) {
          ctx.fillStyle = "rgba(255,255,255,0.95)";
          ctx.font = `500 ${lineHeight}px sans-serif`;
        } else {
          ctx.fillStyle = "rgba(255,255,255,0.65)";
          ctx.font = `${lineHeight - 1}px sans-serif`;
        }
        ctx.fillText(line, padding, y);
      });

      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `foto_${index + 1}_${auditPlan?.store_name}_${auditPlan?.audit_plan}.jpg`;
        a.click();
        URL.revokeObjectURL(url);
      }, "image/jpeg", 0.92);
    } catch (err) {
      console.error("Download failed:", err);
    }
  };

  if (!open || photos.length === 0) return null;

  const current = photos[index];

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1400,
        background: "rgba(0,0,0,0.92)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        userSelect: "none",
      }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      {/* Top bar */}
      <div
        style={{
          position: "absolute",
          top: 0, left: 0, right: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          padding: "8px 12px",
          background: "rgba(0,0,0,0.5)",
          zIndex: 10,
        }}
      >
        <span style={{ color: "#fff", fontFamily: "monospace", fontSize: 14 }}>
          {index + 1} / {photos.length}
        </span>
        <div style={{ display: "flex", gap: 4 }}>
          <Tooltip title="Zoom Out (-)">
            <span>
              <IconButton onClick={zoomOut} disabled={scale <= 0.5} sx={{ color: "#fff" }}>
                <ZoomOut />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Reset Zoom">
            <span>
              <IconButton onClick={resetView} sx={{ color: "#fff" }}>
                <FitScreen />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Zoom In (+)">
            <span>
              <IconButton onClick={zoomIn} disabled={scale >= 5} sx={{ color: "#fff" }}>
                <ZoomIn />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Download foto ini (dengan watermark)">
            <span>
              <IconButton onClick={handleDownloadSingle} sx={{ color: "#fff" }}>
                <Download />
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Tutup (Esc)">
            <IconButton onClick={onClose} sx={{ color: "#fff" }}>
              <Close />
            </IconButton>
          </Tooltip>
        </div>
      </div>

      {/* Prev button */}
      {photos.length > 1 && (
        <IconButton
          onClick={() => goTo(index - 1)}
          sx={{
            position: "absolute", left: 8,
            color: "#fff",
            background: "rgba(255,255,255,0.15)",
            "&:hover": { background: "rgba(255,255,255,0.3)" },
            zIndex: 10,
          }}
        >
          <ChevronLeft sx={{ fontSize: 40 }} />
        </IconButton>
      )}

      {/* Image container — outer flex centering */}
      <div
        ref={containerRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        style={{
          overflow: "hidden",
          cursor: scale > 1 ? (isDragging ? "grabbing" : "grab") : "default",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "90vw",
          height: "85vh",
          maxWidth: 1200,
        }}
      >
        {/* Inner wrapper — shrink-wraps to actual image size */}
        <div
          style={{
            position: "relative",
            display: "inline-flex",
            transform: `scale(${scale}) translate(${translate.x / scale}px, ${translate.y / scale}px)`,
            transformOrigin: "center center",
            transition: isDragging ? "none" : "transform 0.15s ease",
          }}
        >
          <img
            ref={imgRef}
            src={current.file}
            alt={`lightbox-${current.id}`}
            draggable={false}
            style={{
              maxWidth: "90vw",
              maxHeight: "85vh",
              objectFit: "contain",
              display: "block",
              pointerEvents: "none",
            }}
          />

          {/* Floating info card — selalu nempel di pojok foto */}
          {auditPlan && (
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                background: "rgba(0,0,0,0.72)",
                padding: "6px 8px",
                fontSize: 11,
                lineHeight: 1.6,
                zIndex: 20,
                pointerEvents: "none",
              }}
            >
              <div style={{ color: "#0ab9cc", fontFamily: "monospace", fontWeight: 500, marginBottom: 2 }}>
                {auditPlan?.store_code} · {auditPlan?.store_type}
              </div>
              <div style={{ color: "rgba(255,255,255,0.9)", fontWeight: 500 }}>
                {auditPlan?.store_name}
              </div>
              <div style={{ color: "rgba(255,255,255,0.6)", marginTop: 2 }}>
                {auditPlan?.audit_plan}
              </div>
              {auditPlan?.user_full_name_surveyor && (
                <div style={{ color: "rgba(255,255,255,0.5)", marginTop: 3, fontSize: 10 }}>
                  Surveyor: {auditPlan?.user_full_name_surveyor} ({auditPlan?.user_name_surveyor})
                </div>
              )}
              {auditPlan?.user_full_name_qc && (
                <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 10 }}>
                  QC: {auditPlan?.user_full_name_qc} ({auditPlan?.user_name_qc})
                </div>
              )}
              <div style={{ color: "rgba(255,255,255,0.5)", fontSize: 10 }}>
                {auditPlan?.audit_start_at} – {auditPlan?.audit_end_at}
              </div>
              <div
                style={{
                  display: "inline-block",
                  marginTop: 4,
                  background: auditPlan?.audit_status === "Closed"
                    ? "rgba(234,75,74,0.25)" : "rgba(10,185,204,0.25)",
                  color: auditPlan?.audit_status === "Closed" ? "#f09595" : "#0ab9cc",
                  fontSize: 10,
                  padding: "2px 6px",
                  borderRadius: 4,
                  fontFamily: "monospace",
                }}
              >
                {auditPlan?.audit_status}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Next button */}
      {photos.length > 1 && (
        <IconButton
          onClick={() => goTo(index + 1)}
          sx={{
            position: "absolute", right: 8,
            color: "#fff",
            background: "rgba(255,255,255,0.15)",
            "&:hover": { background: "rgba(255,255,255,0.3)" },
            zIndex: 10,
          }}
        >
          <ChevronRight sx={{ fontSize: 40 }} />
        </IconButton>
      )}

      {/* Thumbnail strip */}
      {photos.length > 1 && (
        <div
          style={{
            position: "absolute",
            bottom: 12,
            display: "flex",
            gap: 6,
            overflowX: "auto",
            maxWidth: "90vw",
            padding: "4px 8px",
          }}
        >
          {photos.map((photo, i) => (
            <img
              key={photo.id}
              src={photo.file}
              alt={`thumb-${photo.id}`}
              onClick={() => goTo(i)}
              style={{
                width: 56,
                height: 56,
                objectFit: "cover",
                borderRadius: 4,
                cursor: "pointer",
                border: i === index ? "2px solid #0ab9cc" : "2px solid transparent",
                opacity: i === index ? 1 : 0.55,
                flexShrink: 0,
                transition: "opacity 0.2s, border-color 0.2s",
              }}
            />
          ))}
        </div>
      )}
    </div>
  );
}